import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import "@mantine/carousel/styles.css";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { EmblaCarouselType } from "embla-carousel-react";
import {
  Children,
  ReactNode,
  isValidElement,
  useEffect,
  useState,
} from "react";

type WebinarSectionProps = {
  title: string;
  children: ReactNode;
};

export function WebinarSection({ title, children }: WebinarSectionProps) {
  const slides = Children.toArray(children);
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [snapCount, setSnapCount] = useState(0);
  const [selectedSnap, setSelectedSnap] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Mantine only counts snaps on mount, but they change whenever the
  // slide size changes across breakpoints, so track them from embla directly.
  useEffect(() => {
    if (!embla) return;

    const sync = () => {
      setSnapCount(embla.scrollSnapList().length);
      setSelectedSnap(embla.selectedScrollSnap());
      setCanScrollPrev(embla.canScrollPrev());
      setCanScrollNext(embla.canScrollNext());
    };

    sync();
    embla.on("select", sync);
    embla.on("reInit", sync);
    return () => {
      embla.off("select", sync);
      embla.off("reInit", sync);
    };
  }, [embla]);

  const isScrollable = snapCount > 1;

  return (
    <Box component="section" aria-label={title}>
      <Group justify="space-between" wrap="nowrap" mb="sm">
        <Group gap="xs" wrap="nowrap">
          <Title order={2} size="h4">
            {title}
          </Title>
          <Badge variant="light" color="gray" radius="xl">
            {slides.length}
          </Badge>
        </Group>
        {isScrollable && (
          <Group gap="xs" wrap="nowrap" visibleFrom="sm">
            <ActionIcon
              variant="default"
              radius="xl"
              size="lg"
              aria-label="Previous"
              disabled={!canScrollPrev}
              onClick={() => embla?.scrollPrev()}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              radius="xl"
              size="lg"
              aria-label="Next"
              disabled={!canScrollNext}
              onClick={() => embla?.scrollNext()}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
        )}
      </Group>

      <Carousel
        getEmblaApi={setEmbla}
        withControls={false}
        align="start"
        containScroll="trimSnaps"
        slideGap={{ base: "sm", sm: "md" }}
        // On phones, let the next card peek in so it is obvious the row scrolls.
        slideSize={{
          base: slides.length > 1 ? "88%" : "100%",
          sm: "50%",
          lg: "33.333333%",
        }}
        styles={{ viewport: { paddingBottom: 4 } }}
      >
        {slides.map((slide, index) => (
          <Carousel.Slide
            key={isValidElement(slide) && slide.key !== null ? slide.key : index}
          >
            {slide}
          </Carousel.Slide>
        ))}
      </Carousel>

      {isScrollable && (
        <Group justify="center" gap={6} mt="sm" aria-hidden>
          {Array.from({ length: snapCount }, (_, index) => (
            <UnstyledButton
              key={index}
              tabIndex={-1}
              h={6}
              w={index === selectedSnap ? 20 : 6}
              bg={index === selectedSnap ? "primary.6" : "gray.4"}
              style={{ borderRadius: 3, transition: "width 150ms ease" }}
              onClick={() => embla?.scrollTo(index)}
            />
          ))}
        </Group>
      )}
    </Box>
  );
}
